package com.fantasytenis.web.components.bracket

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.bracket.entity.BracketMatchEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketRoundEntity
import com.fantasytenis.shared.domain.bracket.entity.RoundName
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderBottom
import com.fantasytenis.web.theme.borderLeft
import com.fantasytenis.web.theme.borderRight
import com.fantasytenis.web.theme.borderTop
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Guided View Container - Three-panel layout for efficient pick-making.
 *
 * Layout:
 * +----------------+---------------------------+------------+
 * | NAVIGATION     |    ACTIVE ZONE            | PREVIEW    |
 * | RAIL (~180px)  |    (Focus Area)           | PANE       |
 * |                |                           | (~220px)   |
 * | - Round Pills  |   [Current matches        |            |
 * | - Progress     |    to pick from]          | [Impact]   |
 * | - Quick Jump   |                           | [What's    |
 * |                |                           |  Next]     |
 * +----------------+---------------------------+------------+
 */
@Composable
fun GuidedViewContainer(
    rounds: List<BracketRoundEntity>,
    drawSize: Int,
    picks: Map<String, String>,
    isViewOnly: Boolean,
    onSelectWinner: (positionId: String, winnerId: String) -> Unit,
    onShowMatchDetails: (BracketMatchEntity) -> Unit
) {
    // Calculate feeder sections for early rounds navigation
    val feederSections = remember(rounds, drawSize, picks) {
        FeederSectionCalculator.calculateFeederSections(rounds, drawSize, picks)
    }

    // Navigation state
    var selectedRound by remember { mutableStateOf(rounds.firstOrNull()?.name) }
    var selectedSectionIndex by remember { mutableStateOf(0) }

    // Get current section's matches based on selected round
    val currentMatches = remember(selectedRound, selectedSectionIndex, rounds, feederSections) {
        when {
            // For early rounds (R128/R64), use feeder sections but filter by selected round
            selectedRound in listOf(RoundName.R128, RoundName.R64) && feederSections.isNotEmpty() -> {
                val section = feederSections.getOrNull(selectedSectionIndex)
                // Filter to only show matches from the selected round
                section?.feederMatches
                    ?.find { it.round == selectedRound }
                    ?.matches
                    ?: emptyList()
            }
            // For later rounds, show all matches in that round
            else -> {
                rounds.find { it.name == selectedRound }?.matches ?: emptyList()
            }
        }
    }

    // Calculate progress for current round/section
    val currentPickedCount = currentMatches.count { picks.containsKey(it.player1.id) }
    val currentTotalCount = currentMatches.size

    // Calculate what the current pick advances to
    val advancementInfo = remember(selectedRound, selectedSectionIndex, rounds) {
        calculateAdvancementInfo(selectedRound, selectedSectionIndex, rounds, drawSize)
    }

    Div({
        classes("guided-view-container")
        style {
            display(DisplayStyle.Flex)
            width(100.percent)
            height(100.percent)
            backgroundColor(Colors.Background)
            overflow("hidden")
        }
    }) {
        // Left Panel: Navigation Rail
        RoundNavigationRail(
            rounds = rounds,
            feederSections = feederSections,
            picks = picks,
            selectedRound = selectedRound,
            selectedSectionIndex = selectedSectionIndex,
            onSelectRound = { round ->
                selectedRound = round
                selectedSectionIndex = 0
            },
            onSelectSection = { index ->
                selectedSectionIndex = index
            }
        )

        // Center Panel: Active Zone (matches to pick)
        ActiveZonePanel(
            matches = currentMatches,
            picks = picks,
            isViewOnly = isViewOnly,
            selectedRound = selectedRound,
            sectionIndex = if (feederSections.isNotEmpty() && selectedRound in listOf(RoundName.R128, RoundName.R64)) selectedSectionIndex else null,
            totalSections = if (feederSections.isNotEmpty() && selectedRound in listOf(RoundName.R128, RoundName.R64)) feederSections.size else null,
            pickedCount = currentPickedCount,
            totalCount = currentTotalCount,
            onSelectWinner = onSelectWinner,
            onShowMatchDetails = onShowMatchDetails,
            onPrevSection = {
                if (selectedSectionIndex > 0) selectedSectionIndex--
            },
            onNextSection = {
                if (feederSections.isNotEmpty() && selectedSectionIndex < feederSections.size - 1) {
                    selectedSectionIndex++
                }
            },
            rounds = rounds
        )

        // Right Panel: Impact Preview
        ImpactPreviewPanel(
            picks = picks,
            currentMatches = currentMatches,
            advancementInfo = advancementInfo,
            rounds = rounds
        )
    }
}

/**
 * Left panel with round pills and progress indicators.
 */
@Composable
private fun RoundNavigationRail(
    rounds: List<BracketRoundEntity>,
    feederSections: List<FeederSection>,
    picks: Map<String, String>,
    selectedRound: RoundName?,
    selectedSectionIndex: Int,
    onSelectRound: (RoundName) -> Unit,
    onSelectSection: (Int) -> Unit
) {
    Div({
        classes("navigation-rail")
        style {
            width(180.px)
            flexShrink(0)
            backgroundColor(Colors.Surface)
            borderRight(1.px, LineStyle.Solid, Colors.Border)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            overflowY("auto")
        }
    }) {
        // Header
        Div({
            style {
                padding(16.px)
                borderBottom(1.px, LineStyle.Solid, Colors.Border)
            }
        }) {
            Span({
                style {
                    fontSize(13.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                }
            }) {
                Text("ROUNDS")
            }
        }

        // Round pills
        Div({
            style {
                padding(12.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
            rounds.forEach { round ->
                val roundPicked = round.matches.count { picks.containsKey(it.player1.id) }
                val roundTotal = round.matches.size
                val isComplete = roundPicked == roundTotal
                val isSelected = selectedRound == round.name

                RoundPill(
                    roundName = round.name,
                    pickedCount = roundPicked,
                    totalCount = roundTotal,
                    isComplete = isComplete,
                    isSelected = isSelected,
                    onClick = { onSelectRound(round.name) }
                )
            }
        }

        // Section navigation for early rounds
        if (feederSections.isNotEmpty() && selectedRound in listOf(RoundName.R128, RoundName.R64)) {
            Div({
                style {
                    padding(12.px)
                    borderTop(1.px, LineStyle.Solid, Colors.Border)
                }
            }) {
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(600)
                        color(Colors.TextSecondary)
                        marginBottom(8.px)
                        display(DisplayStyle.Block)
                    }
                }) {
                    Text("SECTIONS")
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(4.px)
                    }
                }) {
                    feederSections.forEachIndexed { index, section ->
                        // Calculate round-specific progress for this section
                        val roundMatches = section.feederMatches
                            .find { it.round == selectedRound }
                            ?.matches
                            ?: emptyList()
                        val roundPicked = roundMatches.count { picks.containsKey(it.player1.id) }
                        val roundTotal = roundMatches.size

                        SectionNavItem(
                            sectionNumber = index + 1,
                            isComplete = roundPicked == roundTotal && roundTotal > 0,
                            progress = "$roundPicked/$roundTotal",
                            isSelected = selectedSectionIndex == index,
                            onClick = { onSelectSection(index) }
                        )
                    }
                }
            }
        }
    }
}

/**
 * Round navigation pill showing round name and progress.
 */
@Composable
private fun RoundPill(
    roundName: RoundName,
    pickedCount: Int,
    totalCount: Int,
    isComplete: Boolean,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    val bgColor = when {
        isSelected -> Colors.Primary
        isComplete -> Colors.SuccessLight
        pickedCount > 0 -> Colors.PrimaryLight
        else -> Colors.Gray100
    }

    val textColor = when {
        isSelected -> Colors.White
        isComplete -> Colors.Success
        pickedCount > 0 -> Colors.Primary
        else -> Colors.TextSecondary
    }

    Div({
        classes("round-pill")
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(10.px, 12.px)
            borderRadius(8.px)
            backgroundColor(bgColor)
            cursor("pointer")
            property("transition", "all 0.2s ease")
        }
        onClick { onClick() }
    }) {
        // Round name
        Span({
            style {
                fontSize(13.px)
                fontWeight(if (isSelected) 600 else 500)
                color(textColor)
            }
        }) {
            Text(roundName.displayName)
        }

        // Progress indicator
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(4.px)
            }
        }) {
            if (isComplete) {
                Span({
                    style {
                        fontSize(12.px)
                        color(if (isSelected) Colors.White else Colors.Success)
                    }
                }) {
                    Text("\u2713")
                }
            }
            Span({
                style {
                    fontSize(11.px)
                    color(if (isSelected) Color("rgba(255,255,255,0.8)") else textColor)
                }
            }) {
                Text("$pickedCount/$totalCount")
            }
        }
    }
}

/**
 * Section navigation item for early rounds.
 */
@Composable
private fun SectionNavItem(
    sectionNumber: Int,
    isComplete: Boolean,
    progress: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(6.px, 10.px)
            borderRadius(6.px)
            backgroundColor(if (isSelected) Colors.PrimaryLight else Color("transparent"))
            cursor("pointer")
        }
        onClick { onClick() }
    }) {
        Span({
            style {
                fontSize(12.px)
                color(if (isSelected) Colors.Primary else Colors.TextSecondary)
            }
        }) {
            Text("Sec $sectionNumber")
        }

        Span({
            style {
                fontSize(10.px)
                color(if (isComplete) Colors.Success else Colors.TextSecondary)
            }
        }) {
            if (isComplete) Text("\u2713") else Text(progress)
        }
    }
}

/**
 * Center panel showing matches to pick from.
 */
@Composable
private fun ActiveZonePanel(
    matches: List<BracketMatchEntity>,
    picks: Map<String, String>,
    isViewOnly: Boolean,
    selectedRound: RoundName?,
    sectionIndex: Int?,
    totalSections: Int?,
    pickedCount: Int,
    totalCount: Int,
    onSelectWinner: (positionId: String, winnerId: String) -> Unit,
    onShowMatchDetails: (BracketMatchEntity) -> Unit,
    onPrevSection: () -> Unit,
    onNextSection: () -> Unit,
    rounds: List<BracketRoundEntity>
) {
    Div({
        classes("active-zone-panel")
        style {
            flex(1)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            overflow("hidden")
        }
    }) {
        // Header with context
        Div({
            style {
                padding(16.px, 20.px)
                backgroundColor(Colors.Surface)
                borderBottom(1.px, LineStyle.Solid, Colors.Border)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            // Title
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                Span({
                    style {
                        fontSize(16.px)
                        fontWeight(600)
                        color(Colors.TextPrimary)
                    }
                }) {
                    val title = if (sectionIndex != null) {
                        "${selectedRound?.displayName ?: ""} - Section ${sectionIndex + 1}"
                    } else {
                        selectedRound?.displayName ?: "Select a round"
                    }
                    Text(title)
                }

                // Progress badge
                Div({
                    style {
                        padding(4.px, 10.px)
                        borderRadius(12.px)
                        backgroundColor(if (pickedCount == totalCount) Colors.SuccessLight else Colors.Gray100)
                    }
                }) {
                    Span({
                        style {
                            fontSize(12.px)
                            fontWeight(500)
                            color(if (pickedCount == totalCount) Colors.Success else Colors.TextSecondary)
                        }
                    }) {
                        if (pickedCount == totalCount && totalCount > 0) {
                            Text("\u2713 $pickedCount/$totalCount")
                        } else {
                            Text("$pickedCount/$totalCount picks")
                        }
                    }
                }
            }

            // Section paginator (if applicable)
            if (sectionIndex != null && totalSections != null) {
                SectionPaginator(
                    currentIndex = sectionIndex,
                    totalSections = totalSections,
                    onPrev = onPrevSection,
                    onNext = onNextSection
                )
            }
        }

        // Match cards grid
        Div({
            style {
                flex(1)
                overflowY("auto")
                padding(20.px)
            }
        }) {
            if (matches.isEmpty()) {
                // Empty state
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        height(100.percent)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Span({ style { fontSize(32.px); marginBottom(12.px) } }) {
                        Text("\uD83C\uDFBE")
                    }
                    Text("Select a round to start making picks")
                }
            } else {
                // Responsive grid: 2 columns for 4 or fewer matches, auto-fill for more
                val gridColumns = when {
                    matches.size <= 2 -> "repeat(auto-fit, minmax(min(100%, 280px), 400px))"
                    matches.size <= 4 -> "repeat(auto-fit, minmax(min(100%, 280px), 1fr))"
                    else -> "repeat(auto-fill, minmax(min(100%, 300px), 1fr))"
                }

                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", gridColumns)
                        gap(16.px)
                        // Center the grid when there are few items
                        if (matches.size <= 4) {
                            maxWidth(800.px)
                            margin(0.px, "auto".unsafeCast<CSSNumeric>())
                        }
                    }
                }) {
                    matches.forEach { match ->
                        // Compute effective display players based on picks (like Tree View does)
                        val (p1Display, p2Display) = PickAdvancementCalculator.getMatchDisplayPlayers(
                            match = match,
                            picks = picks,
                            allRounds = rounds
                        )

                        // Debug: Log what we're displaying for R64 matches
                        if (selectedRound == RoundName.R64) {
                            console.log("R64 Match ${match.matchNumber}: p1Display=${p1Display.name} (seed=${p1Display.seed}), p2Display=${p2Display.name} (seed=${p2Display.seed})")
                        }

                        FocusedMatchCard(
                            match = match,
                            selectedWinnerId = picks[match.player1.id],
                            isViewOnly = isViewOnly,
                            onSelectWinner = { winnerId ->
                                onSelectWinner(match.player1.id, winnerId)
                            },
                            onShowDetails = { onShowMatchDetails(match) },
                            player1Display = p1Display,
                            player2Display = p2Display
                        )
                    }
                }
            }
        }
    }
}

/**
 * Large match card for focused pick selection.
 */
@Composable
fun FocusedMatchCard(
    match: BracketMatchEntity,
    selectedWinnerId: String?,
    isViewOnly: Boolean,
    onSelectWinner: (winnerId: String) -> Unit,
    onShowDetails: () -> Unit,
    player1Display: DisplayPlayer? = null,
    player2Display: DisplayPlayer? = null
) {
    // Use provided display data or fall back to match data
    val effectiveP1 = player1Display ?: DisplayPlayer(
        name = match.player1.displayName,
        seed = match.player1.seed,
        country = match.player1.playerCountry,
        playerId = match.player1.playerId,
        isFromPick = false
    )
    val effectiveP2 = player2Display ?: DisplayPlayer(
        name = match.player2.displayName,
        seed = match.player2.seed,
        country = match.player2.playerCountry,
        playerId = match.player2.playerId,
        isFromPick = false
    )

    val isPicked = selectedWinnerId != null
    val player1Selected = selectedWinnerId == effectiveP1.playerId
    val player2Selected = selectedWinnerId == effectiveP2.playerId

    Div({
        classes("focused-match-card")
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            border(
                2.px,
                LineStyle.Solid,
                if (isPicked) Colors.Success else Colors.Border
            )
            overflow("hidden")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.08)")
        }
    }) {
        // Match header
        Div({
            style {
                padding(10.px, 14.px)
                backgroundColor(if (isPicked) Colors.SuccessLight else Colors.Gray50)
                borderBottom(1.px, LineStyle.Solid, Colors.Border)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Span({
                style {
                    fontSize(12.px)
                    fontWeight(500)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("Match ${match.matchNumber}")
            }

            // Info button
            Span({
                style {
                    fontSize(14.px)
                    cursor("pointer")
                    color(Colors.Primary)
                }
                onClick { onShowDetails() }
            }) {
                Text("\u2139\uFE0F")
            }
        }

        // Player options
        Div({
            style {
                padding(12.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
            // Player 1
            PlayerPickOption(
                name = effectiveP1.name,
                seed = effectiveP1.seed,
                country = effectiveP1.country,
                isSelected = player1Selected,
                isDisabled = isViewOnly || effectiveP1.playerId == null,
                onClick = {
                    effectiveP1.playerId?.let { onSelectWinner(it) }
                }
            )

            // VS divider
            Div({
                style {
                    textAlign("center")
                    color(Colors.TextSecondary)
                    fontSize(11.px)
                    padding(4.px, 0.px)
                }
            }) {
                Text("vs")
            }

            // Player 2
            PlayerPickOption(
                name = effectiveP2.name,
                seed = effectiveP2.seed,
                country = effectiveP2.country,
                isSelected = player2Selected,
                isDisabled = isViewOnly || effectiveP2.playerId == null,
                onClick = {
                    effectiveP2.playerId?.let { onSelectWinner(it) }
                }
            )
        }

        // Points info
        Div({
            style {
                padding(8.px, 14.px)
                backgroundColor(Colors.Gray50)
                borderTop(1.px, LineStyle.Solid, Colors.Border)
                textAlign("center")
            }
        }) {
            Span({
                style {
                    fontSize(11.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("Worth ${match.round.basePoints} point${if (match.round.basePoints > 1) "s" else ""}")
            }
        }
    }
}

/**
 * Player option within a focused match card.
 */
@Composable
private fun PlayerPickOption(
    name: String,
    seed: Int?,
    country: String?,
    isSelected: Boolean,
    isDisabled: Boolean,
    onClick: () -> Unit
) {
    val bgColor = when {
        isSelected -> Colors.SuccessLight
        isDisabled -> Colors.Gray50
        else -> Colors.SurfaceVariant
    }

    val borderColor = when {
        isSelected -> Colors.Success
        else -> Color("transparent")
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            padding(12.px, 14.px)
            borderRadius(8.px)
            backgroundColor(bgColor)
            border(2.px, LineStyle.Solid, borderColor)
            cursor(if (isDisabled) "default" else "pointer")
            property("transition", "all 0.2s ease")
            if (isDisabled) {
                opacity(0.6)
            }
        }
        if (!isDisabled) {
            onClick { onClick() }
        }
    }) {
        // Player info
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(10.px)
            }
        }) {
            // Seed badge
            seed?.let {
                Span({
                    style {
                        backgroundColor(Colors.Primary)
                        color(Colors.White)
                        fontSize(10.px)
                        fontWeight(600)
                        padding(2.px, 6.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text("$it")
                }
            }

            // Name and country
            Div {
                Div({
                    style {
                        fontSize(14.px)
                        fontWeight(if (isSelected) 600 else 500)
                        color(Colors.TextPrimary)
                    }
                }) {
                    Text(name)
                }
                country?.let {
                    Div({
                        style {
                            fontSize(11.px)
                            color(Colors.TextSecondary)
                            marginTop(2.px)
                        }
                    }) {
                        Text(it)
                    }
                }
            }
        }

        // Pick indicator
        if (isSelected) {
            Span({
                style {
                    fontSize(16.px)
                    color(Colors.Success)
                }
            }) {
                Text("\u2713")
            }
        } else if (!isDisabled) {
            Span({
                style {
                    fontSize(12.px)
                    color(Colors.Primary)
                    fontWeight(500)
                }
            }) {
                Text("PICK")
            }
        }
    }
}

/**
 * Section paginator for navigating between sections.
 */
@Composable
fun SectionPaginator(
    currentIndex: Int,
    totalSections: Int,
    onPrev: () -> Unit,
    onNext: () -> Unit
) {
    Div({
        classes("section-paginator")
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        // Previous button
        Div({
            style {
                padding(6.px, 10.px)
                borderRadius(6.px)
                backgroundColor(if (currentIndex > 0) Colors.SurfaceVariant else Colors.Gray100)
                cursor(if (currentIndex > 0) "pointer" else "default")
                opacity(if (currentIndex > 0) 1.0 else 0.5)
            }
            if (currentIndex > 0) {
                onClick { onPrev() }
            }
        }) {
            Text("\u2190 Prev")
        }

        // Current position
        Span({
            style {
                fontSize(13.px)
                color(Colors.TextSecondary)
            }
        }) {
            Text("${currentIndex + 1} / $totalSections")
        }

        // Next button
        Div({
            style {
                padding(6.px, 10.px)
                borderRadius(6.px)
                backgroundColor(if (currentIndex < totalSections - 1) Colors.SurfaceVariant else Colors.Gray100)
                cursor(if (currentIndex < totalSections - 1) "pointer" else "default")
                opacity(if (currentIndex < totalSections - 1) 1.0 else 0.5)
            }
            if (currentIndex < totalSections - 1) {
                onClick { onNext() }
            }
        }) {
            Text("Next \u2192")
        }
    }
}

/**
 * Right panel showing impact of current selection.
 */
@Composable
private fun ImpactPreviewPanel(
    picks: Map<String, String>,
    currentMatches: List<BracketMatchEntity>,
    advancementInfo: AdvancementInfo?,
    rounds: List<BracketRoundEntity>
) {
    // Calculate overall progress
    val totalMatches = rounds.sumOf { it.matches.size }
    val totalPicked = picks.size
    val progressPercent = if (totalMatches > 0) (totalPicked.toDouble() / totalMatches * 100).toInt() else 0

    Div({
        classes("impact-preview-panel")
        style {
            width(220.px)
            flexShrink(0)
            backgroundColor(Colors.Surface)
            borderLeft(1.px, LineStyle.Solid, Colors.Border)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        // Overall progress section
        Div({
            style {
                padding(16.px)
                borderBottom(1.px, LineStyle.Solid, Colors.Border)
            }
        }) {
            Span({
                style {
                    fontSize(11.px)
                    fontWeight(600)
                    color(Colors.TextSecondary)
                    display(DisplayStyle.Block)
                    marginBottom(12.px)
                }
            }) {
                Text("OVERALL PROGRESS")
            }

            // Progress ring
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                // Circular progress indicator
                Div({
                    style {
                        width(80.px)
                        height(80.px)
                        borderRadius(50.percent)
                        backgroundColor(Colors.Gray100)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        position(Position.Relative)
                        property("background", "conic-gradient(${Colors.Success} ${progressPercent * 3.6}deg, ${Colors.Gray200} 0deg)")
                    }
                }) {
                    // Inner white circle
                    Div({
                        style {
                            width(64.px)
                            height(64.px)
                            borderRadius(50.percent)
                            backgroundColor(Colors.White)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                        }
                    }) {
                        Span({
                            style {
                                fontSize(18.px)
                                fontWeight(700)
                                color(Colors.TextPrimary)
                            }
                        }) {
                            Text("$progressPercent%")
                        }
                    }
                }

                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("$totalPicked / $totalMatches picks")
                }
            }
        }

        // Advancement info section
        if (advancementInfo != null) {
            Div({
                style {
                    padding(16.px)
                    borderBottom(1.px, LineStyle.Solid, Colors.Border)
                }
            }) {
                Span({
                    style {
                        fontSize(11.px)
                        fontWeight(600)
                        color(Colors.TextSecondary)
                        display(DisplayStyle.Block)
                        marginBottom(12.px)
                    }
                }) {
                    Text("WINNERS ADVANCE TO")
                }

                Div({
                    style {
                        backgroundColor(Colors.PrimaryLight)
                        padding(12.px)
                        borderRadius(8.px)
                    }
                }) {
                    Div({
                        style {
                            fontSize(14.px)
                            fontWeight(600)
                            color(Colors.Primary)
                            marginBottom(4.px)
                        }
                    }) {
                        Text(advancementInfo.nextRound.displayName)
                    }
                    Span({
                        style {
                            fontSize(12.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text("Match ${advancementInfo.matchRange}")
                    }
                }
            }
        }

        // What's next section
        Div({
            style {
                padding(16.px)
                flex(1)
            }
        }) {
            Span({
                style {
                    fontSize(11.px)
                    fontWeight(600)
                    color(Colors.TextSecondary)
                    display(DisplayStyle.Block)
                    marginBottom(12.px)
                }
            }) {
                Text("WHAT'S NEXT")
            }

            // Show unpicked matches in current view
            val unpickedMatches = currentMatches.filter { !picks.containsKey(it.player1.id) }

            if (unpickedMatches.isEmpty()) {
                Div({
                    style {
                        backgroundColor(Colors.SuccessLight)
                        padding(12.px)
                        borderRadius(8.px)
                        textAlign("center")
                    }
                }) {
                    Span({
                        style {
                            fontSize(16.px)
                            display(DisplayStyle.Block)
                            marginBottom(4.px)
                        }
                    }) {
                        Text("\u2713")
                    }
                    Span({
                        style {
                            fontSize(12.px)
                            color(Colors.Success)
                        }
                    }) {
                        Text("Section complete!")
                    }
                }
            } else {
                Span({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("${unpickedMatches.size} match${if (unpickedMatches.size > 1) "es" else ""} remaining")
                }
            }
        }
    }
}

/**
 * Data class for advancement information.
 */
data class AdvancementInfo(
    val nextRound: RoundName,
    val matchRange: String
)

/**
 * Calculate where winners from current round/section advance to.
 */
private fun calculateAdvancementInfo(
    selectedRound: RoundName?,
    sectionIndex: Int,
    rounds: List<BracketRoundEntity>,
    @Suppress("UNUSED_PARAMETER") drawSize: Int
): AdvancementInfo? {
    if (selectedRound == null) return null

    val nextRound = when (selectedRound) {
        RoundName.R128 -> RoundName.R64
        RoundName.R64 -> RoundName.R32
        RoundName.R32 -> RoundName.R16
        RoundName.R16 -> RoundName.QF
        RoundName.QF -> RoundName.SF
        RoundName.SF -> RoundName.F
        RoundName.F -> return null // Final has no next round
    }

    val matchRange = when (selectedRound) {
        RoundName.R128, RoundName.R64 -> "${sectionIndex + 1}"
        else -> "1-${rounds.find { it.name == nextRound }?.matches?.size ?: 0}"
    }

    return AdvancementInfo(nextRound, matchRange)
}
